<template>
	<div v-if="!loading">
		<h3></h3>

		<div class="flex flex-row flex-wrap" v-if="!loading">
			<div class="col-12 xl:col-7">
				<div class="card">
					<div class="fluid grid">

						<div class="field col-12">
							<a @click="enableNameEdit">
								<i class="pi pi-lock" v-if="nameLocked"></i>
								<i class="pi pi-lock-open" v-if="!nameLocked"></i>
							</a>
							<div class="float-right label" v-if="!this.newUser"># {{ loginId }}</div>
							<label>Login Email</label>
							<form autocomplete="off">
								<InputText v-model.trim="userRecord.loginEmail" :disabled="nameLocked" />
							</form>
						</div>

						<div class="field col-12">
							<label>Login Password</label> <br />
							<form autocomplete="off">
								<InputText v-model.trim="password" @change="passwordChanged = true"/>
							</form>
						</div>

						<div class="field col-12">
							<label>Access Roles</label><br />
							<div class="flex flex-nowrap">
								<ToggleButton v-model="userRecord.isCompassStaff" onLabel="Compass Staff" offLabel="Compass Staff" @change="onAccessRoleChange" />
								&nbsp;
								<ToggleButton v-model="userRecord.isExternalCustomer" onLabel="External Customer" offLabel="External Customer" @change="onAccessRoleChange" />
								&nbsp;
								<ToggleButton v-model="userRecord.isExternalSupplier" onLabel="External supplier" offLabel="External Supplier" @change="onAccessRoleChange" />
							</div>
						</div>

						<div class="field col-12" v-if="!newUser">
							<label>Last login</label>
							<InputText v-model="formattedLastLoginDateTime" :disabled="true" />
						</div>

						<div class="field col-12" v-if="!newUser">
							<Button class="float-right" label="Delete Login" @click="deleteLogin" />
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- Floating Save/Cancel buttons -->
	<Button class="sticky-button cancel" icon="pi pi-times" :label="hasChanges ? 'Cancel' : 'Close'" @click="cancel" v-if="!loading" />

	<!--Only visible to internal user-->
	<template v-if="$store.getters.isInternalUser">
		<Button class="sticky-button" icon="pi pi-check" label="Save" @click="save" v-if="!loading && !newUser" :disabled="!isRoleValid || !hasChanges" />
		<Button class="sticky-button" icon="pi pi-check" label="Add new record" @click="saveNewUser" v-if="!loading && newUser" :disabled="!isRoleValid || !hasChanges" />
	</template>

</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { CompassApi } from '@/api-client/CompassApiProxy';
	import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
	import { UserData, ChangePasswordRequest, NewUserData, UpdateUserDetailsCommand, AddUserCommand } from '../api-client/api-client';
	import { allowLeaveOnlyWithConfirmationOrNoChanges } from '@/components/LeaveWithoutSavingPrompt';
	import { format, isValid } from 'date-fns';
	import Toast from '@/utils/Toast';

	Vue.registerHooks([
		'beforeRouteLeave',
	]);

	@Options({
		props: {
			loginId: 0
		}
	})
	export default class UserDetails extends Vue {

		loginId!: number;  // Actually passed as a prop

		password: string | null = null;

		newUser = false;

		receivedDataJSON = "";
		userRecord: UserData = new UserData();

		isStaff = false;
		isExternalCustomer = false;
		isExternalSupplier = false;

		passwordChanged = false;

		nameLocked = true;

		loading = true;

		created() {
			this.password = null;
			if (this.loginId < 0) {
				this.newUser = true;

				this.nameLocked = false;

				this.loading = false;

				// So we can detect if there are changes:
				this.receivedDataJSON = JSON.stringify(this.userRecord, null, 1);

				return;
			}

			// Retrieve data from API
			const apiClient = CompassApi.userApi(this.$store);
			apiClient.getUserById(this.loginId)
				.then(result => {
					if (result) {
						this.loading = false;
						this.userRecord = result;
					}

					this.receivedDataJSON = JSON.stringify(result, null, 1);
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError('Error loading user ' + this.loginId, error);
				});
		}

		beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
			allowLeaveOnlyWithConfirmationOrNoChanges(this, this.hasChanges, next);
		}

		enableNameEdit(): void {
			this.nameLocked = !this.nameLocked;
		}


		// Actions
		save() {
			// Clear all toast messages
			Toast.RemoveAllGroups();

			// exit if it's not internal user
			if (!this.$store.getters.isInternalUser) {
				return;
			}

			this.loading = true;

			// If there are changes to password, then attempt changing the password first ... (the function will call updateUserDetails too)
			if (this.passwordChanged) {
				this.changePassword();
			}
			else {
				this.updateUserDetails();
			}
		}

		updateUserDetails() {
			const updateUserDetailsCommand = new UpdateUserDetailsCommand();
			updateUserDetailsCommand.userDetails = this.userRecord

			const apiClient = CompassApi.userApi(this.$store);
			apiClient.updateUserDetails(updateUserDetailsCommand)
				.then(() => {
					this.loading = false;

					this.receivedDataJSON = JSON.stringify(this.userRecord, null, 1);

					Toast.RemoveAllGroups();
					Toast.Success('Update user', 'User details have been updated succesfully');
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError('Error updating details for user ' + this.loginId, error);
				});
			
		}

		deleteLogin() {
			this.userRecord.whenDeleted = new Date;
			this.save();

			// Need to update the JSON so that we bypass the beforeRouteLeave hook
			this.receivedDataJSON = JSON.stringify(this.userRecord, null, 1);
			this.$router.go(-1);
		}

		saveNewUser() {
			// exit if it's not internal user
			if (!this.$store.getters.isInternalUser) {
				return;
			}

			const newUserRecord: NewUserData = new NewUserData();
			newUserRecord.loginEmail = this.userRecord.loginEmail;
			newUserRecord.password = this.password ? this.password : "";
			newUserRecord.accessRoles = this.userRecord.accessRoles ? this.userRecord.accessRoles : '';

			const addUserCommand: AddUserCommand = new AddUserCommand();
			addUserCommand.userDetails = newUserRecord;

			// create user record
			const apiClient = CompassApi.userApi(this.$store);
			apiClient.addUser(addUserCommand)
				.then(result => {
					this.loading = false;

					// result has the new companyid
					const newUrl = window.location.href.replace("/new", "/" + JSON.stringify(result));
					window.location.replace(newUrl);
				})
				.catch(error => {
					this.loading = false;

					if (error.status == 400) {
						Toast.Error('Error creating user ' + this.userRecord.loginEmail, error.response);
					}
					else {
						Toast.ApiError('Error creating user ' + this.userRecord.loginEmail, error);
					}
				});
		}

		changePassword() {
			if (this.password) {

				const requestModel: ChangePasswordRequest = new ChangePasswordRequest();
				requestModel.loginId = this.loginId;
				requestModel.password = this.password;
				requestModel.passwordReEntered = this.password;

				// Change the password
				const apiClient = CompassApi.userApi(this.$store);
				apiClient.changePassword(requestModel)
					.then(() => {

						this.loading = false;

						// reset passwordChanged flag
						this.passwordChanged = false;

						// On successful password change update user details
						this.updateUserDetails();
					})
					.catch(error => {
						this.loading = false;

						if (error.status == 400) {
							Toast.Error('Error changing password for user ' + this.userRecord.loginEmail, error.response);
						}
						else {
							Toast.ApiError('Error changing password for user `' + this.userRecord.loginEmail + '`', error);
						}
					});
			}
		}

		cancel() {
			this.$router.go(-1);
		}

		onAccessRoleChange() {
			let roles = "";
			if (this.userRecord.isCompassStaff) {
				roles += "CompassStaff;"
			}

			if (this.userRecord.isExternalCustomer) {
				roles += "ExternalCustomer;"
			}

			if (this.userRecord.isExternalSupplier) {
				roles += "ExternalSupplier;"
			}

			this.userRecord.accessRoles = roles;
		}

		get isRoleValid(): boolean {
			return typeof this.userRecord.accessRoles === 'string' && this.userRecord.accessRoles.trim().length !== 0
		}

		get hasChanges(): boolean {
			if (this.passwordChanged) {
				return true;
			}
			const currentJSON = JSON.stringify(this.userRecord, null, 1);
			return (currentJSON != this.receivedDataJSON);
		}

		get formattedLastLoginDateTime() {
			if (!isValid(this.userRecord.whenLastLogin)) return '';

			if (this.userRecord.whenLastLogin)
				return format(this.userRecord.whenLastLogin, 'HH:mm:ss dd MMMM yyyy');
		
			return '';
		}
	}
</script>

<style scoped lang="scss">
</style>